import { isEqual } from "lodash"
import React, { useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"
import { Thumbnail, type ThumbnailProps } from "../Thumbnail"
import type { BaseProps, BorderProps } from "../types/props"

interface WrapperProps {
  doWrap: boolean
}
const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-wrap: ${(props) => (props.doWrap ? "wrap" : "nowrap")};
`
interface StyledThumbnailProps {
  offset: string
  hoverScale?: number
}
const StyledThumbnail = styled(Thumbnail)<StyledThumbnailProps>`
  margin-right: ${(props) => props.offset};

  ${(props) =>
    typeof props.hoverScale !== "undefined" &&
    css`
      transition: transform 0.2s ease;
      will-change: transform;

      &:hover {
        transform: scale(${props.hoverScale});
      }
    `}
`

export interface ThumbnailListSource
  extends Pick<ThumbnailProps, "src" | "alt" | "href" | "shape"> {
  altRender?: () => React.ReactNode
}
export interface ThumbnailListProps
  extends Pick<ThumbnailProps, "size" | "shadow" | "cursor" | "shape">,
    BorderProps,
    BaseProps {
  srcList: ThumbnailListSource[]

  offset?: string
  scaleOnHover?: boolean | number
  wrap?: boolean

  onLoadError?: (src: ThumbnailListSource) => void
}

export const ThumbnailList: React.FC<ThumbnailListProps> = ({
  className,
  id,

  srcList,

  offset = "-10px",
  scaleOnHover = false,
  wrap = false,

  size,
  shadow,
  cursor,
  shape,

  border,
  borderColor,
  borderStyle,
  borderWidth,

  onLoadError = () => void 0,
}) => {
  type ThumbnailListSourceOrRender = Omit<ThumbnailListSource, "src"> & {
    render: string | (() => React.ReactNode | null)
  }

  const prevSrcList = useRef<ThumbnailListSource[] | null>(null)

  const hoverScale =
    scaleOnHover !== false
      ? scaleOnHover === true
        ? 1.14
        : scaleOnHover
      : undefined

  function testImage(src: ThumbnailListSource) {
    return new Promise<[boolean, ThumbnailListSource]>((resolve) => {
      const image = new Image()
      const successHandler = () => {
        image.removeEventListener("load", successHandler)
        resolve([true, src])
      }

      const errorHandler = () => {
        image.removeEventListener("error", errorHandler)
        resolve([false, src])
      }

      image.addEventListener("load", successHandler)
      image.addEventListener("error", errorHandler)
      image.src = src.src
    })
  }

  const [verifiedImages, setVerifiedImages] = useState<
    ThumbnailListSourceOrRender[]
  >([])

  useEffect(() => {
    if (isEqual(prevSrcList.current, srcList)) return
    prevSrcList.current = srcList

    const ret: ThumbnailListSourceOrRender[] = []
    const tests = srcList.map((src) => testImage(src))

    Promise.all(tests).then((results) => {
      results.forEach(([success, src]) => {
        if (!success) {
          ret.push({
            ...src,
            render: src.altRender ?? (() => null),
          })
          onLoadError(src)
        } else
          ret.push({
            ...src,
            render: src.src,
          })
      })
      setVerifiedImages(ret)
    })
  }, [srcList, onLoadError])

  return (
    <Wrapper className={className} id={id} doWrap={wrap}>
      {verifiedImages.map((src, index) => {
        return typeof src.render === "string" ? (
          <StyledThumbnail
            key={src.render + index}
            className="--thumbnail-list-thumb"
            offset={offset}
            hoverScale={hoverScale}
            size={size}
            shadow={shadow}
            cursor={cursor}
            shape={shape}
            border={border}
            borderColor={borderColor}
            borderStyle={borderStyle}
            borderWidth={borderWidth}
            {...src}
            src={src.render}
          />
        ) : (
          <React.Fragment key={index}>{src.render()}</React.Fragment>
        )
      })}
    </Wrapper>
  )
}
